import { CartProduct, FarmProduct, useCartState } from 'public/context/CartContext';
import {CouponDiscounts} from "../interfaces/Coupon";

type CartProductPriceType = 'PER_UNIT' | 'TOTAL';

interface ProductPriceTotalProps {
  totalWithDiscount: number;
  totalWithoutDiscount: number;
  totalVat: number;
  totalWithCoupon?: number; // total after coupon discount if no other discounts apply
}

type GroupedVatProps = {
  vatRate: number;
  totalVat: number;
};

export interface PriceTotalProps extends ProductPriceTotalProps {
  groupedVats: GroupedVatProps[];
}

const calculateProductPriceTotals = (
  product: CartProduct,
  type: CartProductPriceType = 'TOTAL',
  coupons: CouponDiscounts[] = []
): ProductPriceTotalProps => {
  const { amount, cartProduct, vat } = product;

  const {
    storage_stock_price = 0,
    storage_stock_discount_price,
    storage_wholesale_limit,
    storage_wholesale_price,
  } = cartProduct ?? {};

  let totalVat = 0;
  let totalWithDiscount = storage_stock_price;
  const totalWithoutDiscount = storage_stock_price;

  if (
    storage_stock_discount_price &&
    storage_stock_discount_price < totalWithDiscount
  ) {
    totalWithDiscount = storage_stock_discount_price;
  }

  if (
    storage_wholesale_limit &&
    storage_wholesale_price &&
    amount >= storage_wholesale_limit &&
    storage_wholesale_price < totalWithDiscount
  ) {
    totalWithDiscount = storage_wholesale_price;
  }

  if (vat) {
    totalVat = totalWithDiscount * (vat / 100);
  }

  // Optional coupon discount when no other discounts apply
  const hasOwnDiscount = totalWithDiscount < totalWithoutDiscount;
  const perUnitCoupon = coupons.reduce(
    (sum, c) => sum + (c.productDiscounts?.[cartProduct?.id as number] ?? 0),
    0
  );
  const couponDiscount = type === 'PER_UNIT'
    ? perUnitCoupon
    : perUnitCoupon * amount;
  const totalWithCoupon = !hasOwnDiscount && couponDiscount > 0
    ? (type === 'PER_UNIT'
        ? totalWithDiscount - couponDiscount
        : totalWithDiscount * amount - couponDiscount)
    : undefined;

  if (type === 'PER_UNIT') {
    return {
      totalVat,
      totalWithDiscount,
      totalWithoutDiscount,
      ...(totalWithCoupon !== undefined && { totalWithCoupon }),
    };
  }

  return {
    totalVat: totalVat * amount,
    totalWithDiscount: totalWithDiscount * amount,
    totalWithoutDiscount: totalWithoutDiscount * amount,
    ...(totalWithCoupon !== undefined && { totalWithCoupon }),
  };
}

export const calculateFarmPriceTotals = (
  products: CartProduct[],
  coupons: CouponDiscounts[] = []
): PriceTotalProps => {
  return products.reduce<PriceTotalProps>(
    (acc, product) => {
      const productPriceTotals = calculateProductPriceTotals(product, 'TOTAL', coupons);

      acc.totalWithDiscount += productPriceTotals.totalWithDiscount;
      acc.totalWithoutDiscount += productPriceTotals.totalWithoutDiscount;
      acc.totalVat += productPriceTotals.totalVat;

      if (product.vat && productPriceTotals.totalVat > 0) {
        const vatGroup = acc.groupedVats.find(
          (vatGroup) => vatGroup.vatRate === product.vat
        );

        if (vatGroup) {
          vatGroup.totalVat += productPriceTotals.totalVat;
        } else {
          const newVatGroup: GroupedVatProps = {
            vatRate: 21,
            totalVat: productPriceTotals.totalVat,
          };

          acc.groupedVats.push(newVatGroup);
        }
      }

      return acc;
    },
    {
      totalWithDiscount: 0,
      totalWithoutDiscount: 0,
      totalVat: 0,
      groupedVats: [],
    }
  );
};

export const calculatePriceTotals = (
  farmProducts: FarmProduct[]
): PriceTotalProps => {
  return farmProducts.reduce<PriceTotalProps>(
    (acc, farm) => {
      acc.totalWithDiscount += farm.farmPriceTotals.totalWithDiscount;
      acc.totalWithoutDiscount += farm.farmPriceTotals.totalWithoutDiscount;
      acc.totalVat += farm.farmPriceTotals.totalVat;

      Object.values(farm.farmPriceTotals.groupedVats).forEach((groupedVat) => {
        const vatGroup = acc.groupedVats.find(
          (vatGroup) => vatGroup.vatRate === groupedVat.vatRate
        );

        if (vatGroup) {
          vatGroup.totalVat += groupedVat.totalVat;
        } else {
          const newVatGroup: GroupedVatProps = {
            vatRate: groupedVat.vatRate,
            totalVat: groupedVat.totalVat,
          };

          acc.groupedVats.push(newVatGroup);
        }
      });
      const defaultVatGroup = acc.groupedVats.find(
        (vatGroup) => vatGroup.vatRate === 21
      );

      if (defaultVatGroup) {
        defaultVatGroup.totalVat +=
          ((farm.selectedDelivery?.price ?? 0) * 21) / 100;
      } else {
        const newVatGroup: GroupedVatProps = {
          vatRate: 21,
          totalVat: ((farm.selectedDelivery?.price ?? 0) * 21) / 100,
        };

        acc.groupedVats.push(newVatGroup);
      }
      acc.totalVat += ((farm.selectedDelivery?.price ?? 0) * 21) / 100;

      return acc;
    },
    {
      totalWithDiscount: 0,
      totalWithoutDiscount: 0,
      totalVat: 0,
      groupedVats: [],
    }
  );
};

export const calculateTotalPrice = (farmProducts: FarmProduct[]): number => {
  return Number(
    farmProducts
      .reduce(
        (total, farm) =>
          total +
          (farm.farmPriceTotals?.totalWithDiscount || 0) +
          (farm.selectedDelivery?.price || 0),
        0
      )
      .toFixed(2)
  );
};

interface CartProductPriceProps {
  product: CartProduct;
  type?: CartProductPriceType;
}

export const CartProductPrice = ({
  product,
  type = 'TOTAL',
}: CartProductPriceProps) => {
  const { coupons } = useCartState();
  const {
    totalWithoutDiscount,
    totalWithDiscount,
    totalWithCoupon,
  } = calculateProductPriceTotals(product, type, coupons);

  const hasCouponPrice = typeof totalWithCoupon === 'number';
  const finalPrice = hasCouponPrice ? totalWithCoupon : totalWithDiscount;
  const hasAnyDiscount = hasCouponPrice || totalWithDiscount < totalWithoutDiscount;

  const unitLabel =
    type === 'PER_UNIT' && product.cartProduct?.storage_unit
      ? `\u00A0/\u00A0${product.cartProduct.storage_unit}`
      : null;

  if (!hasAnyDiscount) {
    return (
      <div className="price-type-container d-flex flex-row">
        <div className="price">{totalWithoutDiscount.toFixed(2)} <span>€</span></div>
        {unitLabel && <div className="type-of-price">{unitLabel}</div>}
      </div>
    );
  }

  return (
    <div className="price-type-wrapper d-flex flex-col">
      <div className="price-type-second d-flex flex-row">
        <div className="price original">{totalWithoutDiscount.toFixed(2)} €</div>
        {unitLabel && <div className="type-of-price">{unitLabel}</div>}
      </div>
      <div className="price-grey grey-price d-flex flex-row">
        <div className="price">{finalPrice.toFixed(2)} <span>€</span></div>
        {unitLabel && <div className="type-of-price">{unitLabel}</div>}
      </div>
    </div>
  );
};
